import { useDictionaryContext } from '3-foundation/Dictionary/DictionaryContext';
import { defaultEventsLimit, getEvents } from '3-foundation/Events/repository';
import { usePageContext } from '3-foundation/PageContent/PageContext';
import { useState, useCallback, useEffect } from 'react';
import { UI_FilterList } from 'ui/components/2-molecules/FilterList/FilterList';
import { UI_EventOverview } from 'ui/components/3-organisms/EventOverview/EventOverview';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { useSiteContext } from '3-foundation/Site/SiteContext';
import { useRouter } from 'next/router';

export const EventSearchResults = ({
	serverSideEvents,
	tags,
	totalEvents,
}: {
	serverSideEvents: Content.EventCatalogPageProperties['events'];
	tags: Content.EventCatalogPageProperties['filterTags'];
	totalEvents: Content.EventCatalogPageProperties['totalEvents'];
}) => {
	const pageContext = usePageContext();
	const siteContext = useSiteContext();
	const dictionary = useDictionaryContext();
	const { locale } = useRouter();

	const [clientSideEvents, setClientSideEvents] = useState(serverSideEvents);
	const allEventsFilter = { name: dictionary.getEvent('AllEvents'), count: totalEvents, selected: true };
	const [filters, setFilters] = useState<{ name: string; count: number; selected?: boolean }[]>([allEventsFilter, ...tags]);
	const [fetchClientSide, setFetchClientSide] = useState(false);

	const handleFilterClick = (tag) => {
		setFetchClientSide(true);
		setFilters((prevFilters) => {
			let anySelected = false;

			const updatedFilters = prevFilters.map((filter) => {
				const isClicked = filter.name === tag;
				const selected = isClicked ? !(filter.selected ?? false) : false;

				if (selected) anySelected = true;
				return { ...filter, selected };
			});

			// If no filter ended up selected, default the first one to true
			if (!anySelected && updatedFilters.length > 0) {
				updatedFilters[0].selected = true;
			}

			setClientSideEvents([]); // Clear EventsResults since we will use EventsItems instead
			return updatedFilters;
		});
	};

	const fetchEvents = useCallback(
		async ({ limit }: { limit?: number }) => {
			try {
				const searchFilters = filters?.filter((f) => f.selected).filter((sf) => sf.name !== allEventsFilter.name) || [];
				const eventsData = await getEvents({
					filters: {
						parentId: pageContext?.id,
						tagsSubject: searchFilters.map((sf) => sf.name),
					},
					limit,
					locale,
					siteId: siteContext?.root?.id,
				});
				setClientSideEvents(eventsData.items);
			} catch (error) {
				console.error('Failed to fetch events:', error);
			}
		},
		[filters, pageContext?.id, allEventsFilter?.name, locale, siteContext?.root?.id],
	);
	useEffect(() => {
		if (!fetchClientSide) return;
		const allEventsFilterSelected = filters?.some((filter) => filter.name === allEventsFilter.name && filter.selected);
		if (allEventsFilterSelected) return;
		fetchEvents({});
	}, [allEventsFilter.name, fetchClientSide, fetchEvents, filters]);

	const currentEvents = clientSideEvents.length > 0 ? clientSideEvents : serverSideEvents;
	const activeFilter = filters.find((f) => f.selected) ?? allEventsFilter;

	return (
		<UI_Container width="Standard">
			<UI_FilterList filters={[...filters]} onClick={(filter) => handleFilterClick(filter)} />
			<UI_EventOverview
				cards={currentEvents?.map((event) => {
					return {
						...event,
						date: {
							...event.date,
							label: dictionary?.getEvent('Date'),
						},
						location: {
							...event.location,
							label: dictionary?.getEvent('Location'),
						},
						price: {
							...event.price,
							label: dictionary?.getEvent('Price'),
						},
						link: { ...event.link, name: dictionary.getEvent('MoreInfo') },
					};
				})}
				paginationText={currentEvents.length < activeFilter.count ? dictionary.getSearch('LoadMore') : null}
				requestMoreCards={() => fetchEvents({ limit: currentEvents.length + defaultEventsLimit })}
			/>
		</UI_Container>
	);
};
